// src/pages/packageDefinitions/components/PackageDefinitionsTable.jsx
import React, { useMemo, useRef, useState, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { useLanguage } from "../../../contexts/LanguageContext";

const PackageDefinitionsTable = ({
  rows = [],
  loading = false,
  filters,
  totalCount = 0,
  pagination = { PageNumber: 1, PageSize: 10 },
  onPageChange = () => {},
  handleEdit = () => {}
}) => {
  const { th } = useLanguage();
  const dataTableRef = useRef(null);
  const columnsRef = useRef(null);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(0);

  const getColumnsCallback = useCallback((el) => {
    if (el && el.getColumns && !columnsRef.current) {
      columnsRef.current = el.getColumns();
    }
    dataTableRef.current = el;
  }, []);

  const actionBodyTemplate = (row) => {
    const isEditable = row.isEditable !== false; 
    const iconClass = isEditable ? "pi-pencil" : "pi-eye";
    const titleKey = isEditable ? "packageDefinitions.edit" : "packageDefinitions.view";
    
    return (
      <div className="flex items-center gap-3">
        <i
          onClick={() => handleEdit(row)}
          className={`pi ${iconClass} cursor-pointer text-blue-500 hover:text-blue-700`}
          title={th(titleKey)}
        />
      </div>
    );
  };

  const usageTemplate = (row) => (
    <Tag
      value={row.isInUse ? th("common.active") : th("common.inactive")}
      severity={row.isInUse ? "success" : "danger"}
    />
  );

  const normalized = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const sorted = useMemo(() => {
    if (!sortField || sortOrder === 0) return normalized;
    const copy = [...normalized];
    return copy.sort((a, b) => {
      let av = a?.[sortField];
      let bv = b?.[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return sortOrder;
      if (bv == null) return -sortOrder;
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return -sortOrder;
      if (av > bv) return sortOrder;
      return 0;
    });
  }, [normalized, sortField, sortOrder]);

  const handleSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
  };

  return (
    <DataTable
      key={`pkg-table-${sorted.length}-${loading}-${sortField}-${sortOrder}`}
      value={sorted}
      dataKey="id"
      lazy
      paginator
      first={(pagination?.PageNumber - 1) * (pagination?.PageSize || 10)}
      rows={pagination?.PageSize || 10}
      totalRecords={totalCount}
      onPage={onPageChange}
      onSort={handleSort}
      sortField={sortField}
      sortOrder={sortOrder}
      loading={loading}
      filters={filters}
      filterDisplay="menu"
      rowsPerPageOptions={[10, 20, 50]}
      showGridlines
      stripedRows
      sortMode="single"
      ref={getColumnsCallback}
      emptyMessage={th("packageDefinitions.empty")}
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate={th("table.paginatorTemplate")}
    >
      <Column body={actionBodyTemplate} exportable={false} style={{ width: "5%", minWidth: "53px" }} />
      <Column field="packageName" header={th("packageDefinitions.packageName")} sortable />
      <Column field="description" header={th("packageDefinitions.description")} sortable />
      <Column field="groupName" header={th("packageDefinitions.group")} sortable />
      <Column field="isInUse" header={th("packageDefinitions.usageInfo")} body={usageTemplate} sortable />
    </DataTable>
  );
};

export default PackageDefinitionsTable;
